import React from 'react';
import {
  columnLabel,
  conditionHasColumn,
  isFastIdScoreColumn,
  opIsInLast,
  opIsMulti,
  opLabel,
  opNeedsNoValue,
  parseListValue,
  typeFamily,
} from './criteriaUtils';

function fmtValue(v) {
  const s = String(v ?? '').trim();
  return s === '' ? '…' : s;
}

function joinList(items) {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
}

/** Describe one condition as `{ column, operator, value }` display strings. */
export function describeCondition(condition) {
  const column = conditionHasColumn(condition) ? columnLabel(condition) : 'a column';
  const op = String(condition.operator || '').trim().toLowerCase();
  let operator = opLabel(op, condition.column_type);
  let value = '';

  if (isFastIdScoreColumn(condition)) {
    operator = opLabel(op, 'int');
    if (op === 'between') value = `${fmtValue(condition.value)} and ${fmtValue(condition.value2)}`;
    else if (!opNeedsNoValue(op)) value = fmtValue(condition.value);
  } else if (opNeedsNoValue(op)) {
    value = '';
  } else if (opIsMulti(op)) {
    const list = parseListValue(condition.value);
    if (list.length === 0) value = '…';
    else if (list.length <= 4) value = joinList(list);
    else value = `${list.slice(0, 3).join(', ')} + ${list.length - 3} more`;
  } else if (opIsInLast(op)) {
    value = `${fmtValue(condition.value)} ${String(condition.value2 || 'days')}`;
  } else if (op === 'between') {
    value = `${fmtValue(condition.value)} and ${fmtValue(condition.value2)}`;
  } else {
    value = fmtValue(condition.value);
    if (typeFamily(condition.column_type) === 'string' && value !== '…') value = `“${value}”`;
  }
  return { column, operator, value };
}

/**
 * Render a criteria tree as an English sentence fragment, e.g.
 *   **last signal** is in the last 30 days **and** (**domain** is any of gmail.com or yahoo.com **or** …)
 */
export function CriteriaSentence({ criteria, className = '' }) {
  const parts = renderNode(criteria, true);
  if (!parts) {
    return <span className={`text-zinc-400 ${className}`}>No conditions yet.</span>;
  }
  return <span className={className}>Profiles where {parts}</span>;
}

function renderNode(node, isRoot = false, keyPrefix = 'n') {
  if (!node) return null;
  if (node.kind === 'condition') {
    const d = describeCondition(node);
    return (
      <span key={keyPrefix} className="whitespace-normal">
        <span className="font-semibold text-[#18181B]">{d.column}</span>{' '}
        <span className="text-zinc-600">{d.operator}</span>
        {d.value ? (
          <>
            {' '}
            <span className="text-[#18181B]">{d.value}</span>
          </>
        ) : null}
      </span>
    );
  }
  const children = (node.children || []).map((c, i) => renderNode(c, false, `${keyPrefix}-${i}`)).filter(Boolean);
  if (!children.length) return null;
  const joiner = node.op === 'OR' ? 'or' : 'and';
  const out = [];
  children.forEach((child, i) => {
    if (i > 0) {
      out.push(
        <span key={`${keyPrefix}-j${i}`} className="mx-1 rounded bg-[#E8EEF5] px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          {joiner}
        </span>
      );
    }
    out.push(child);
  });
  if (isRoot || children.length === 1) return <>{out}</>;
  return (
    <span key={keyPrefix}>
      <span className="text-zinc-400">(</span>
      {out}
      <span className="text-zinc-400">)</span>
    </span>
  );
}

/** Plain-text version (for list summaries, titles, NL round-trips). */
export function criteriaToText(node, isRoot = true) {
  if (!node) return '';
  if (node.kind === 'condition') {
    const d = describeCondition(node);
    return [d.column, d.operator, d.value].filter(Boolean).join(' ');
  }
  const parts = (node.children || []).map((c) => criteriaToText(c, false)).filter(Boolean);
  if (!parts.length) return '';
  const joined = parts.join(node.op === 'OR' ? ' or ' : ' and ');
  return isRoot || parts.length === 1 ? joined : `(${joined})`;
}
